import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/siteConfig';

const featuredArticles = articles.filter((a) => a.featured);
const latestArticles = articles.slice(0, 6);

const heroJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `${siteConfig.siteName} - ${siteConfig.siteDescription}`,
  description: siteConfig.siteDescription,
  url: siteConfig.siteUrl,
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '什么是秘密教学？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '秘密教学是一套专注于揭示隐藏知识体系的方法论框架。它系统化了那些传统教育中不被公开传授的高阶认知模式、隐性知识传递方式和深度学习策略，帮助学习者突破常规学习的瓶颈。',
      },
    },
    {
      '@type': 'Question',
      name: '秘密教学适合什么样的人群？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '秘密教学特别适合那些感觉常规学习方式无法满足深度认知需求的求知者，包括但不限于：追求高阶思维能力的专业人士、希望突破职业瓶颈的管理者、对认知科学有浓厚兴趣的研究者，以及所有渴望超越"知道"层面达到"内化"状态的深度学习爱好者。',
      },
    },
    {
      '@type': 'Question',
      name: '秘密教学与普通学习方法有什么不同？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '秘密教学与普通学习方法有五个核心差异：1）选择性公开而非一次性全部呈现；2）渐进式揭示而非线性灌输；3）实践先行而非理论前置；4）情境嵌入而非抽象说教；5）反思内化而非被动接收。这五大原则共同构成了秘密教学的独特优势。',
      },
    },
    {
      '@type': 'Question',
      name: '如何开始学习秘密教学方法论？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '建议从阅读"核心理念"分类下的文章开始，理解秘密教学的起源与五大核心原则。然后根据自身需求选择"实践方法"或"思维训练"分类中的文章进行针对性学习。最重要的是，在学习过程中保持实践导向——将每一个方法论立即应用到实际场景中。',
      },
    },
    {
      '@type': 'Question',
      name: '秘密教学的内容多久更新一次？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们每周至少发布2-3篇经过深度研究和精心撰写的新文章，内容涵盖核心理念解读、实践方法分享、应用案例分析以及认知科学最新研究成果。同时我们会定期对已有内容进行修订和补充，确保知识体系的完整性和前沿性。',
      },
    },
  ],
};

export default function Home() {
  return (
    <>
      <SEOHead
        title={`${siteConfig.siteName}：${siteConfig.siteDescription}`}
        description={siteConfig.siteDescription}
        keywords="秘密教学,秘密教学方法论,隐秘知识体系,非主流学习法,高阶认知训练,深度学习秘籍,突破学习瓶颈"
        jsonLd={[heroJsonLd, faqJsonLd]}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20abstract%20landscape%20with%20soft%20golden%20light%20rays%20streaming%20through%20geometric%20shapes%2C%20cream%20and%20warm%20amber%20gradient%20background%2C%20ethereal%20atmosphere%2C%20inspiring%20and%20contemplative%20mood%2C%20high%20quality%20digital%20art%20with%20clean%20composition&width=1800&height=1200&seq=hero-bg-01&orientation=landscape"
            alt="秘密教学 - 隐藏知识体系的深度学习指南"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
          <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              发现隐藏知识的深度力量
            </h1>
            <p className="text-sm md:text-lg text-white/80 max-w-xl mx-auto leading-relaxed mb-8">
              揭开那些不被公开传授的隐秘知识体系，掌握普通课堂之外的高阶认知框架
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/core-philosophy"
                className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer"
              >
                开始探索
              </Link>
              <Link
                to="/about"
                className="whitespace-nowrap bg-white/15 hover:bg-white/25 text-white px-6 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3">
                探索知识体系
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                五大核心分类，系统化构建你的秘密教学认知框架
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-100 hover:bg-primary-50 rounded-lg p-5 transition-colors cursor-pointer border border-background-200/70 hover:border-primary-200"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-lg`} />
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                    {cat.description}
                  </p>
                  <p className="text-xs text-foreground-500 mt-3">
                    {cat.articleCount} 篇文章
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-2">
                  精选深度文章
                </h2>
                <p className="text-sm text-foreground-600">
                  为你精选的最具启发性的秘密教学深度内容
                </p>
              </div>
              <Link
                to="/category/core-philosophy"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
              >
                查看全部 <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-2">
                  最新文章
                </h2>
                <p className="text-sm text-foreground-600">
                  持续更新的秘密教学知识库，每周为你带来新的认知突破
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-accent-500">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '40+', label: '深度文章' },
                { value: '5', label: '核心分类' },
                { value: '15,000+', label: '月度读者' },
                { value: '4.9', label: '读者评分' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-1">
                    {stat.value}
                  </p>
                  <p className="text-sm text-background-50/80">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">
                关于秘密教学，你可能想了解的几个问题
              </p>
            </div>
            <div className="flex flex-col gap-4">
              {faqJsonLd.mainEntity.map((item, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="font-heading text-sm font-semibold text-foreground-900 pr-4">
                      {item.name}
                    </span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-500">
                      <i className="ri-add-line group-open:hidden text-sm" />
                      <i className="ri-subtract-line hidden group-open:block text-sm" />
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.acceptedAnswer.text}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3">
              准备好突破你的学习瓶颈了吗？
            </h2>
            <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
              加入我们，与成千上万渴望深度成长的求知者一起，探索那些被隐藏在常规教育背后的高阶认知世界。
            </p>
            <Link
              to="/category/practice-methods"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-7 py-3.5 rounded-md text-sm font-medium transition-colors cursor-pointer"
            >
              立即开始你的秘密教学之旅 <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}